import React, { useState, useEffect } from 'react';
import { ArrowLeftIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../../contexts/BusinessTypeContext';
import { useMasterData, useMutation } from '../../../hooks/useApi';
import LoadingSpinner from '../../../components/UI/LoadingSpinner';
import apiClient from '../../../utils/api';

interface GoatFeedFormData {
  shed_id: string;
  feed_date: string;
  quantity_kg: number;
  feed_brand: string;
  feed_type?: string;
  remarks?: string;
  location_id?: string;
}

interface GoatFeedLog {
  id: string;
  shed_id: string;
  feed_date: string;
  quantity_kg: number;
  feed_brand: string;
  feed_type?: string;
  remarks?: string;
  created_at: string;
  created_by: string;
}

interface ShedFeedData {
  id: string;
  name: string;
  capacity?: number;
  quantity_kg: string;
  feed_brand: string;
  remarks: string;
  expanded: boolean;
}

const GoatFeedForm: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const [feedDate, setFeedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [shedsData, setShedsData] = useState<ShedFeedData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { data: feedBrands } = useMasterData('goat_feed_brands', selectedLocation?.id || null);
  const [inventoryBalances, setInventoryBalances] = useState<any[]>([]);
  const { mutate: createFeedLog, loading: creating } = useMutation<GoatFeedLog, GoatFeedFormData>();

  // Load goat sheds
  useEffect(() => {
    if (selectedLocation?.id && businessType === 'goat') {
      loadSheds();
    }
  }, [selectedLocation?.id, businessType]);

  const loadSheds = async () => {
    try {
      const response = await apiClient.get(`/goat/sheds?location_id=${selectedLocation?.id}`);
      const sheds = response.data || [];
      setShedsData(sheds.map((shed: any) => ({
        id: shed.id,
        name: shed.name,
        capacity: shed.capacity,
        quantity_kg: '',
        feed_brand: '',
        remarks: '',
        expanded: true  // Keep all sheds open by default for mobile-friendly experience
      })));
    } catch (err: any) {
      setError('Failed to load goat sheds');
    }
  };

  // Load inventory balances for selected location
  useEffect(() => {
    const loadBalances = async () => {
      if (!selectedLocation) {
        setInventoryBalances([]);
        return;
      }
      try {
        const res = await apiClient.get('/inventory/balances', {
          params: { location_id: selectedLocation.id },
        });
        setInventoryBalances(res.data || []);
      } catch {
        setInventoryBalances([]);
      }
    };
    loadBalances();
  }, [selectedLocation]);

  // Build a quick lookup for inventory on hand by brand name
  const feedBrandStockByName: Record<string, number> = React.useMemo(() => {
    const stock: Record<string, number> = {};
    (inventoryBalances || []).forEach((b: any) => {
      if (b?.item_name) stock[b.item_name] = b.on_hand_quantity || 0;
    });
    return stock;
  }, [inventoryBalances]);

  // Show all feed brands from master data; annotate with stock info
  const availableFeedBrands = (feedBrands || []) as any[];

  const updateShed = (shedId: string, field: keyof ShedFeedData, value: string | boolean) => {
    setShedsData(shedsData.map(shed => 
      shed.id === shedId ? { ...shed, [field]: value } : shed
    ));
  };

  const toggleShedExpansion = (shedId: string) => {
    setShedsData(shedsData.map(shed => 
      shed.id === shedId ? { ...shed, expanded: !shed.expanded } : shed
    ));
  };

  // Bulk actions for efficiency
  const copyToAllSheds = (field: keyof ShedFeedData, value: string) => {
    setShedsData(shedsData.map(shed => ({ ...shed, [field]: value })));
  };

  const clearAllSheds = () => {
    setShedsData(shedsData.map(shed => ({ 
      ...shed, 
      quantity_kg: '', 
      feed_brand: '', 
      remarks: '' 
    })));
  };

  const handleSubmit = async () => {
    const validEntries = shedsData.filter(shed => shed.quantity_kg && shed.feed_brand);
    
    if (validEntries.length === 0) {
      alert('Please fill feed data (Feed and Quantity) for at least one shed');
      return;
    }

    try {
      setLoading(true);
      // Use goat-specific feed log endpoint
      const promises = validEntries.map(shed => 
        createFeedLog('/goat/feed-logs', {
          shed_id: shed.id,
          feed_date: feedDate,
          quantity_kg: parseFloat(shed.quantity_kg),
          feed_brand: shed.feed_brand,
          feed_type: shed.feed_brand, // Use feed_brand value for feed_type
          remarks: shed.remarks || undefined,
          location_id: selectedLocation?.id,
        } as any)
      );
      
      await Promise.all(promises);
      alert(`✅ Successfully recorded feed data for ${validEntries.length} sheds on ${feedDate}`);
      
      // Reset form
      setShedsData(shedsData.map(shed => ({ 
        ...shed, 
        quantity_kg: '', 
        feed_brand: '', 
        remarks: '' 
      })));
    } catch (err: any) {
      setError(err.message || 'Failed to save feed log');
    } finally {
      setLoading(false);
    }
  };

  const getTotalQuantity = () => {
    return shedsData.reduce((sum, shed) => sum + (parseFloat(shed.quantity_kg) || 0), 0).toFixed(1);
  };

  const getFilledShedsCount = () => {
    return shedsData.filter(shed => shed.quantity_kg && shed.feed_brand).length;
  };

  if (businessType !== 'goat') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This form is only available for goat farming locations.</p>
        <Link to="/goat/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (!selectedLocation) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  if (shedsData.length === 0) {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600 mb-4">No goat sheds found for this location.</p>
        <Link to="/goat/sheds/new" className="text-blue-600 hover:text-blue-800">
          Create a shed first
        </Link>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link to="/goat/data-entry/" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Goat Feed Log</h1>
          <p className="text-gray-600">{selectedLocation.name}</p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Date Selection */}
      <div className="card p-4">
        <label className="form-label">Feed Date</label>
        <input
          type="date"
          value={feedDate}
          onChange={(e) => setFeedDate(e.target.value)}
          className="form-input"
        />
      </div>

      {/* Bulk Actions */}
      {shedsData.length > 1 && (
        <div className="card p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Bulk Actions</h3>
          <div className="flex flex-wrap gap-2">
            <select
              onChange={(e) => {
                if (e.target.value) {
                  copyToAllSheds('feed_brand', e.target.value);
                  e.target.value = '';
                }
              }}
              className="form-input text-sm"
            >
              <option value="">Copy Feed to All</option>
              {availableFeedBrands.map((brand: any) => (
                <option key={brand.id} value={brand.name}>
                  {brand.name} {feedBrandStockByName[brand.name] !== undefined ? `(${feedBrandStockByName[brand.name]} kg)` : ''}
                </option>
              ))}
            </select>
            <button
              onClick={clearAllSheds}
              className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {/* Sheds List */}
      <div className="space-y-3">
        {shedsData.map((shed) => (
          <div key={shed.id} className="card p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{shed.name}</h3>
                {shed.capacity && (
                  <p className="text-sm text-gray-500">Capacity: {shed.capacity} goats</p>
                )}
              </div>
              <button
                onClick={() => toggleShedExpansion(shed.id)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                {shed.expanded ? (
                  <ChevronUpIcon className="h-5 w-5" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            {shed.expanded && (
              <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
                <div>
                  <label className="form-label">Feed *</label>
                  <select
                    value={shed.feed_brand}
                    onChange={(e) => updateShed(shed.id, 'feed_brand', e.target.value)}
                    className="form-input"
                    required
                  >
                    <option value="">Select Feed</option>
                    {availableFeedBrands.map((brand: any) => (
                      <option key={brand.id} value={brand.name}>
                        {brand.name} {feedBrandStockByName[brand.name] !== undefined ? `(${feedBrandStockByName[brand.name]} kg available)` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="form-label">Quantity (kg) *</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    value={shed.quantity_kg}
                    onChange={(e) => updateShed(shed.id, 'quantity_kg', e.target.value)}
                    className="form-input"
                    placeholder="0.0"
                  />
                </div>

                <div>
                  <label className="form-label">Remarks</label>
                  <textarea
                    value={shed.remarks}
                    onChange={(e) => updateShed(shed.id, 'remarks', e.target.value)}
                    className="form-input"
                    rows={2}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="card p-4 bg-blue-50">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-gray-600">Total Quantity</p>
            <p className="text-2xl font-bold text-gray-900">{getTotalQuantity()} kg</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Filled Sheds</p>
            <p className="text-2xl font-bold text-gray-900">{getFilledShedsCount()} / {shedsData.length}</p>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        disabled={loading || creating || getFilledShedsCount() === 0}
        className="w-full btn-touch bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
      >
        {(loading || creating) ? (
          <LoadingSpinner size="sm" />
        ) : (
          `Save Feed Log (${getFilledShedsCount()} sheds)`
        )}
      </button>
    </div>
  );
};

export default GoatFeedForm;
